/**
 * E.164 parse/format + locale-detect helpers for the shared `PhoneField`
 * (DNKADM-0084). Pure functions over the curated `./countries` list — no phone
 * library, no network/geo-IP (the spec's lean-deps + no-geo decisions).
 *
 * Canonical storage is E.164 (`+<dialCode><digits>`); the national-number mask is
 * DISPLAY ONLY (see {@link formatNational}). Parsing tolerates a legacy/unparseable
 * stored value by showing it AS-IS — it never blanks, guesses, or auto-rewrites a
 * value (see {@link parsePhone}); a clean value is only ever produced by an admin
 * edit through the picker.
 *
 * The locale default mirrors the `resolvedTimeZone()` idiom in
 * `src/features/locations/timezones.ts`: read `Intl` defensively and degrade to a
 * safe fallback (here, the US/NANP default) when the runtime can't resolve one.
 */
import {
  COUNTRIES,
  countryByIso,
  defaultCountry,
  type Country,
} from './countries';

/** Strip everything but digits from a string (drops `+`, spaces, parens, dashes). */
export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

/**
 * Build the canonical E.164 value from a country + the typed national number.
 * Returns `''` when there are no national digits (an empty phone stays empty so
 * the call-site omits the optional key — never a bare `+<dialCode>`).
 */
export function toE164(country: Country, national: string): string {
  const digits = digitsOnly(national);
  return digits === '' ? '' : `+${country.dialCode}${digits}`;
}

/**
 * Apply a country's grouping mask to the national digits for DISPLAY. `X` in the
 * mask is a digit slot; any other character is a literal separator emitted only
 * once the next digit is reached (so no trailing separators hang off a partial
 * number). Digits beyond the mask are appended verbatim (a longer-than-expected
 * number still shows in full rather than being truncated).
 */
export function formatNational(format: string, digits: string): string {
  if (digits === '') return '';
  let out = '';
  let di = 0;
  for (const ch of format) {
    if (di >= digits.length) break;
    if (ch === 'X') {
      out += digits[di];
      di += 1;
    } else {
      out += ch;
    }
  }
  // Any overflow digits (number longer than the mask) are appended as-is.
  if (di < digits.length) out += digits.slice(di);
  return out;
}

/** The parsed shape: the selected country + the national value to show. */
export interface ParsedPhone {
  /** The country to select in the picker. */
  country: Country;
  /**
   * The string to show in the national field — formatted digits for a parsed
   * E.164 value, or a legacy value shown verbatim (correctable via the picker).
   */
  national: string;
}

/**
 * The curated countries sorted by dial-code length DESCENDING, so a longest-prefix
 * match wins (e.g. `+506…` matches Costa Rica before any shorter code). Computed
 * once — the list is static.
 */
const BY_DIAL_LENGTH_DESC = [...COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length,
);

/**
 * Parse a stored phone value into the country to select + the national value to
 * show, given the default country to fall back to.
 *
 *  - **Empty** → the default country, empty national.
 *  - **E.164** (`+` then digits) whose digits start with a curated dial code →
 *    that country (longest-prefix match; `+1` defaults to the first `+1` entry,
 *    i.e. the US per the NANP note) + the remainder formatted via the country's
 *    mask.
 *  - **Anything else** (a legacy/free-text value like `"512-555-1234"`, or a `+`
 *    value whose code isn't curated, or a GHL-reformatted member phone) → the
 *    default country with the value shown **AS-IS** so the admin sees it and can
 *    correct it. Never blanked, never auto-rewritten, never a guessed country.
 */
export function parsePhone(value: string, fallback: Country = defaultCountry()): ParsedPhone {
  const trimmed = value.trim();
  if (trimmed === '') return { country: fallback, national: '' };

  if (trimmed.startsWith('+')) {
    const digits = digitsOnly(trimmed);
    const match = BY_DIAL_LENGTH_DESC.find((c) => digits.startsWith(c.dialCode));
    if (match) {
      const national = digits.slice(match.dialCode.length);
      return { country: match, national: formatNational(match.format, national) };
    }
  }

  // Legacy / unparseable / non-curated code: show it as-is under the default.
  return { country: fallback, national: trimmed };
}

/**
 * Format a stored phone value for DISPLAY (read-only surfaces, e.g. the member
 * header). A parsed E.164 value renders as `+<dialCode> <grouped national>`; a
 * legacy/unparseable or non-curated value (including a GHL-reformatted member
 * phone) is shown verbatim — never blanked, never a guessed prefix. Empty → `''`.
 */
export function formatPhoneForDisplay(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (trimmed.startsWith('+')) {
    const digits = digitsOnly(trimmed);
    const match = BY_DIAL_LENGTH_DESC.find((c) => digits.startsWith(c.dialCode));
    if (match) {
      const national = formatNational(match.format, digits.slice(match.dialCode.length));
      return `+${match.dialCode} ${national}`.trim();
    }
  }
  return trimmed;
}

/**
 * Read `navigator.language`/`languages` defensively and extract a 2-letter region
 * subtag (e.g. `en-US` → `US`), or `null` when none is resolvable. Never throws.
 */
function localeRegion(): string | null {
  try {
    const nav =
      typeof navigator === 'undefined'
        ? undefined
        : (navigator as Navigator & { languages?: readonly string[] });
    const tag = nav?.languages?.[0] ?? nav?.language;
    if (typeof tag !== 'string' || tag === '') return null;
    // `Intl.Locale` gives the canonical region when present; fall back to a manual
    // `xx-REGION` split if the runtime lacks it.
    try {
      const region = new Intl.Locale(tag).region;
      if (typeof region === 'string' && region.length === 2) return region.toUpperCase();
    } catch {
      // fall through to the manual parse
    }
    const parts = tag.split('-');
    const region = parts.length > 1 ? parts[1] : undefined;
    return region !== undefined && region.length === 2 ? region.toUpperCase() : null;
  } catch {
    return null;
  }
}

/**
 * The country to default a NEW (empty-value) phone to: the browser locale's region
 * when it maps to a curated country, else the US/NANP fallback. No network/geo-IP
 * — purely `Intl`/`navigator`, mirroring `resolvedTimeZone()`'s guarded idiom.
 */
export function detectDefaultCountry(): Country {
  const region = localeRegion();
  if (region !== null) {
    const match = countryByIso(region);
    if (match) return match;
  }
  return defaultCountry();
}
